import { useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete01Icon, RecoveryMailIcon } from '@hugeicons/core-solid-rounded'
import { Trans, useLingui } from '@lingui/react/macro'

import Dropdown from '@/components/ui/Dropdown'
import { Suspense } from '@/components/ui/Suspense'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useMembershipsWithSubscription } from '../hooks/useMemberships'
import { useInvites } from '../hooks/useInvites'
import { ROLES, RoleToLabel } from '../utils/helpers'
import { AddMemberButton } from './AddMemberButton'
import { Permission, useCheckPermission } from '@/features/auth'

function MemberPreview({
  role,
  email,
  fullName,
  isExpired = false,
  onChangeRole,
  onResend,
  onDelete
}: {
  role: GraphQL.Role
  email: string
  fullName?: string
  isExpired?: boolean
  onChangeRole?: (newRole: GraphQL.Role) => void
  onResend?: () => void
  onDelete?: () => void
}) {
  const { t } = useLingui()

  return (
    <div className="flex flex-row items-center gap-2.5 p-4 rounded-small border border-text-light">
      <div className="flex flex-col flex-1">
        <h4 className="text-text text-regular">{fullName ?? email}</h4>
        {fullName && (
          <span className="text-text-light text-small">{email}</span>
        )}
        {isExpired && (
          <span className="text-red-500 text-small">
            <Trans>The Invitation expired</Trans>
          </span>
        )}
      </div>
      <div>
        {onChangeRole && (
          <Dropdown options={ROLES} onChange={onChangeRole} value={role} />
        )}
        {!onChangeRole && (
          <span className="text-text text-regular">{RoleToLabel[role]}</span>
        )}
      </div>
      {onResend && (
        <button tabIndex={0} onClick={onResend}>
          <HugeiconsIcon icon={RecoveryMailIcon} className="size-6 text-text" />
        </button>
      )}
      {onDelete && (
        <ConfirmDialog
          title={t`Delete member`}
          message={t`Are you sure that you want to delete the member "${email}", they will lose all rights?`}
          onConfirm={onDelete}
        >
          <HugeiconsIcon icon={Delete01Icon} className="size-6 text-text" />
        </ConfirmDialog>
      )}
    </div>
  )
}

function Member({
  member,
  user,
  isLastAdmin,
  canUpdate,
  canDelete,
  onUpdate,
  onDelete
}: {
  member: GraphQL.MembershipFragmentFragment
  user: GraphQL.UserFragmentFragment
  isLastAdmin: boolean
  canUpdate: boolean
  canDelete: boolean
  onUpdate: (role: GraphQL.Role) => void
  onDelete: () => void
}) {
  return (
    <MemberPreview
      email={user.email}
      fullName={`${user.givenName} ${user.lastName}`}
      role={member.role}
      // We can't let them delete or change the role of the last Admin
      onChangeRole={isLastAdmin || !canUpdate ? undefined : onUpdate}
      onDelete={isLastAdmin || !canDelete ? undefined : onDelete}
    />
  )
}

function Invite({
  invite,
  canUpdate,
  canDelete,
  onUpdate,
  onResend,
  onDelete
}: {
  invite: GraphQL.MembershipInviteFragmentFragment
  canUpdate: boolean
  canDelete: boolean
  onUpdate: (role: GraphQL.Role) => void
  onResend: () => void
  onDelete: () => void
}) {
  const isExpired = useMemo(() => {
    const date = new Date(invite.expiresAt)
    const now = new Date()

    return date <= now
  }, [invite.expiresAt])

  return (
    <MemberPreview
      email={invite.email}
      role={invite.role}
      isExpired={isExpired}
      onChangeRole={canUpdate ? onUpdate : undefined}
      onResend={canUpdate ? onResend : undefined}
      onDelete={canDelete ? onDelete : undefined}
    />
  )
}

function Members({
  canUpdate,
  canDelete
}: {
  canUpdate: boolean
  canDelete: boolean
}) {
  const { members, onUpdate, onDelete } = useMembershipsWithSubscription()

  const lastAdminEmail = useMemo(() => {
    const admins = members.filter(member => member.role === GraphQL.Role.Admin)

    if (admins.length === 1) return admins[0].user.email
    else return null
  }, [members])

  return (
    <div className="flex flex-col gap-1 p-2">
      {members.map(function (member) {
        return (
          <Member
            key={member.user?.email}
            isLastAdmin={lastAdminEmail === member.user?.email}
            user={member.user}
            member={member}
            canDelete={canDelete}
            canUpdate={canUpdate}
            onUpdate={role => onUpdate(member.id, role)}
            onDelete={() => onDelete(member.id)}
          />
        )
      })}
    </div>
  )
}

function Invites({
  canUpdate,
  canDelete
}: {
  canUpdate: boolean
  canDelete: boolean
}) {
  const { invites, onResend, onUpdate, onDelete } = useInvites()

  return (
    <div className="flex flex-col gap-1 p-2">
      {invites.map(function (invite) {
        return (
          <Invite
            key={invite.email}
            invite={invite}
            canDelete={canDelete}
            canUpdate={canUpdate}
            onResend={() => onResend(invite.id)}
            onUpdate={role => onUpdate(invite.id, role)}
            onDelete={() => onDelete(invite.id)}
          />
        )
      })}
      {invites.length === 0 && (
        <div className="flex flex-row justify-center items-center h-14">
          <span className="text-text-light">
            <Trans>You currently have not Invitations</Trans>
          </span>
        </div>
      )}
    </div>
  )
}

export function MemberList() {
  const canCreateMembers = useCheckPermission([Permission.MEMBERSHIP_CREATE])
  const canUpdateMembers = useCheckPermission([Permission.MEMBERSHIP_UPDATE])
  const canDeleteMembers = useCheckPermission([Permission.MEMBERSHIP_DELETE])

  return (
    <div className="flex flex-col gap-10 p-2">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-2.5">
          {canCreateMembers && <AddMemberButton />}
          <h3 className="text-heading3 text-text">
            <Trans>Invited Members</Trans>
          </h3>
        </div>
        <Suspense>
          <Invites canDelete={canDeleteMembers} canUpdate={canUpdateMembers} />
        </Suspense>
      </div>
      <div className="flex flex-col gap-4">
        <h3 className="text-heading3 text-text">
          <Trans>Current Members</Trans>
        </h3>
        <Suspense>
          <Members canDelete={canDeleteMembers} canUpdate={canUpdateMembers} />
        </Suspense>
      </div>
    </div>
  )
}
